import { Permission } from '@/features/role/type';
import { deleteData, getData, postData, putData } from '@/utils/fetch';
import { computed, ref } from 'vue';
import { PermissionCode, PermissionParam, Roles, Business, SubApps } from './type';
import { getEmailAppList } from '@/api/bill/email';
import useNewSms from '@/hooks/use-new-sms';
import { useCnSite } from '@/hooks/use-cn-site';

/**
 * 根据 isNewSms 状态动态处理权限JSON数据
 * @param permissionData 原始权限数据
 * @param isNewSms 是否使用新SMS
 */
const processPermissionData = (
  permissionData: { default: Array<Permission> },
  isNewSms: boolean,
) => {
  const data = JSON.parse(JSON.stringify(permissionData)); // 深拷贝避免修改原数据

  const filterAndReplacePermissions = (permissions: Permission[]): Permission[] => {
    return permissions
      .map(permission => {
        const newPermission = { ...permission };

        if (isNewSms) {
          // 新SMS模式：替换_SMS为_NEWSMS
          if (newPermission.code?.includes('_SMS')) {
            newPermission.code = newPermission.code.replace('_SMS', '_NEWSMS');
          }
        } else {
          // 旧SMS模式：过滤掉NEWSMS相关权限
          if (newPermission.code?.includes('_NEWSMS')) {
            return null; // 标记为删除
          }
        }

        // 递归处理children
        if (newPermission.children) {
          newPermission.children = filterAndReplacePermissions(newPermission.children);
        }

        return newPermission;
      })
      .filter(permission => permission !== null) as Permission[];
  };

  data.default = filterAndReplacePermissions(data.default);
  return data;
};

/**
 * 获取角色列表
 * @param OrgId 组织ID
 */
export const getRoles = (OrgId: string) =>
  getData<Array<Roles>>(`/api/console/permission/role/o-${OrgId}/list`);

/**
 * 创建角色
 * @param OrgId 组织ID
 * @param name 角色名
 */
export const createRole = (OrgId: string, name: string) =>
  postData<undefined>(`/api/console/permission/role/o-${OrgId}/create`, { name });
/**
 * 编辑角色名
 * @param OrgId 组织ID
 * @param roleId 角色ID
 * @param name 角色名
 */
export const updateRoleName = (OrgId: string, roleId: string, name: string) =>
  postData<undefined>(
    `/api/console/permission/role/o-${OrgId}/${roleId}/editName`,
    { name },
    false,
  );

/**
 * 删除角色
 * @param OrgId 组织ID
 * @param roleId 角色ID
 */
export const deleteRole = (OrgId: string, roleId: string) =>
  deleteData<{ affectCount: number }>(`/api/console/permission/role/o-${OrgId}/${roleId}/remove`);

/**
 * 获取全量权限
 * @param business 业务
 */
export const getFullPermission = () => {
  const { isNewSms } = useNewSms();
  const { cnEdition } = useCnSite();
  return new Promise<Record<Business, { default: Array<Permission> }>>(async resolve => {
    const consolePermissionData = require('@/features/role/console.json');
    const processedConsoleData = processPermissionData(consolePermissionData, isNewSms.value);

    const promiseList = cnEdition
      ? [
          Promise.resolve(processedConsoleData),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
          getData('/console/app-push/menu.json'),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
          Promise.resolve({ default: [] }),
        ]
      : [
          Promise.resolve(processedConsoleData),
          getData('/console/sms/menu.json'),
          getData('/console/email/menu.json'),
          getData('/console/whatsapp/menu.json'),
          getData('/console/app-push/menu.json'),
          getData('/console/web-push/menu.json'),
          getData('/console/otp/menu.json'),
          getData('/console/marketing-automation/menu.json'),
          getData('/console/newsms/menu.json'),
          getData('/console/livedesk/menu.json'),
          getData('/console/silent-auth/menu.json'),
        ];

    Promise.all<{ default: Array<Permission> }>(promiseList).then(
      ([Console, SMS, Email, WhatsApp, AppPush, WebPush, OTP, MA, NEWSMS, LiveDesk, SilentAuth]) =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        resolve({
          Console,
          SMS,
          Email,
          WhatsApp,
          AppPush,
          WebPush,
          OTP,
          MA,
          NEWSMS,
          LiveDesk,
          SilentAuth,
        }),
    );
  });
};

/**
 * 查询角色对应的权限
 * @param OrgId 组织ID
 * @param roleId 角色ID
 */
export const getPermissions = (OrgId: string, roleId: string) =>
  getData<Array<PermissionCode>>(`/api/console/permission/role/o-${OrgId}/permissions/query`, {
    roleId,
  });

/**
 * 保存角色对应的权限
 * @param OrgId 组织ID
 * @param permissions 权限
 * @param roleId 角色ID
 */
export const savePermissions = (
  OrgId: string,
  permissions: Array<PermissionParam>,
  roleId: string,
) =>
  postData(`/api/console/permission/role/o-${OrgId}/permissions/save`, {
    permissions,
    roleId,
  });

/**
 * 解除成员和角色关系
 * @param OrgId 组织ID
 * @param accountId 账号ID
 * @param email 邮箱
 * @param roleId 角色ID
 */
export const deleteRoleFromMember = (
  OrgId: string,
  param: { accountId: string; email: string; roleId: string },
) =>
  deleteData<{ affectCount: number }>(
    `/api/console/organization/member/o-${OrgId}/role/remove`,
    param,
  );

/**
 * 生成子应用列表接口
 * @param subApp 子应用名称
 * @param OrgId 组织ID
 */
export const createGetAppsListApi = (
  subApp:
    | 'WhatsApp'
    | 'AppPush'
    | 'WebPush'
    | 'OTP'
    | 'MA'
    | 'Email'
    | 'NEWSMS'
    | 'LiveDesk'
    | 'SilentAuth',
  orgId: string,
) => {
  switch (subApp) {
    case 'WhatsApp':
      return getData<SubApps>('/api/whatsapp/v1/waba/list', undefined, true, {
        orgId,
      });
    case 'AppPush':
      return getData<SubApps>(
        '/api/app-push/portalapi/v1/portal/devs/engagelab_appInfo',
        undefined,
        true,
        {
          orgId,
        },
      );
    case 'WebPush':
      return getData<SubApps>(
        '/api/web-push/portalapi/v1/portal/devs/engagelab_appInfo',
        undefined,
        true,
        {
          orgId,
        },
      );
    case 'OTP':
      return getData<{ id: string }[]>('/api/otp/v1/business', undefined, true, {
        orgId,
      });
    case 'NEWSMS':
      return getData<{ id: string }[]>('/api/newsms/v1/business', undefined, true, {
        orgId,
      });
    case 'MA':
      return getData<{ id: string }[]>(
        '/api/ma-global/portal/auth/project/selection-list',
        undefined,
        true,
        {
          'x-el-org-id': orgId,
        },
      );
    case 'Email':
      return getEmailAppList(orgId);
    case 'LiveDesk':
      return getData<{ id: string }[]>(
        '/api/ma-global/portal/auth/project/selection-list',
        undefined,
        true,
        {
          'x-el-org-id': orgId,
        },
      );
    case 'SilentAuth':
      return getData<{ id: string }[]>('/api/silent-auth/v1/account/apps', undefined, true, {
        orgId,
      });
    default:
      return Promise.resolve([]);
  }
};
